import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Autocomplete,
  TextField,
  Avatar,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  PlayArrow as GenerateIcon,
  Close as CloseIcon,
  MusicNote as MusicIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { SongClueCard } from "./StyledComponents";
import { styled } from "@mui/material/styles";

/** Show this many clues initially to avoid lag when there are hundreds */
const CLUES_INITIAL_SHOW = 50;

const BulkSelectButton = styled(Button)(() => ({
  textTransform: "none",
  borderRadius: 999,
  paddingInline: 10,
  paddingBlock: 4,
  fontSize: 12,
}));

const SongCluesSection = ({
  question,
  filteredSongs,
  songsLoading,
  questionClues,
  selectedSongsForClues,
  generatingClues,
  onBulkSelectSongs,
  onSongsChange,
  onGenerateClues,
  onRefreshClues,
  onDeleteClue,
}) => {
  const tempQuestionId = question.temp_question_id;
  const clues = questionClues[tempQuestionId] || [];
  const selectedSongs = selectedSongsForClues[tempQuestionId] || [];
  const isGenerating = generatingClues[tempQuestionId] || false;

  const [accordionOpen, setAccordionOpen] = useState(false);
  const [cluesExpanded, setCluesExpanded] = useState(false);
  const showAllClues = cluesExpanded || clues.length <= CLUES_INITIAL_SHOW;
  const visibleClues = showAllClues ? clues : clues.slice(0, CLUES_INITIAL_SHOW);
  const hiddenCount = clues.length - CLUES_INITIAL_SHOW;

  const availableSongs = filteredSongs.filter((song) => {
    const hasClue = clues.some(
      (c) => c.songs_master_id === song.songs_master_id
    );
    return !hasClue;
  });

  return (
    <Box mb={3}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ color: "#4CAF50", fontWeight: 600 }}
        >
          Song Clues
        </Typography>
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          onClick={() => onRefreshClues(tempQuestionId)}
          sx={{ textTransform: "none" }}
        >
          Refresh
        </Button>
      </Box>

      {/* Song Selector */}
      <Box mb={2}>
        <Typography variant="body2" sx={{ color: "#aaa", mb: 1 }}>
          Select songs to generate clues for:
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "flex-start", sm: "center" }}
          sx={{ mb: 1 }}
        >
          <Typography variant="caption" sx={{ color: "#666", mr: 1 }}>
            Quick select:
          </Typography>
          {[50, 100, 150, 200].map((count) => (
            <BulkSelectButton
              key={count}
              size="small"
              variant="outlined"
              onClick={() => onBulkSelectSongs(tempQuestionId, count)}
            >
              {count}
            </BulkSelectButton>
          ))}
          <BulkSelectButton
            size="small"
            variant="contained"
            color="success"
            onClick={() => onBulkSelectSongs(tempQuestionId, "all")}
          >
            Select All
          </BulkSelectButton>
        </Stack>
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={availableSongs}
          loading={songsLoading}
          getOptionLabel={(song) => song.name || ""}
          value={filteredSongs.filter((song) =>
            selectedSongs.includes(song.songs_master_id)
          )}
          onChange={(_, newValue) => {
            onSongsChange(
              tempQuestionId,
              newValue.map((song) => song.songs_master_id)
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select songs"
              placeholder="Type song name or artist…"
              size="small"
            />
          )}
          renderTags={() => null}
        />
        <Stack direction="row" spacing={1} mt={1}>
          <Button
            size="small"
            variant="contained"
            color="secondary"
            startIcon={<GenerateIcon />}
            onClick={() => onGenerateClues(tempQuestionId)}
            disabled={!selectedSongs.length || isGenerating}
          >
            {isGenerating ? "Generating..." : "Generate Clues"}
          </Button>
          {selectedSongs.length > 0 && (
            <Button
              size="small"
              variant="text"
              onClick={() => onSongsChange(tempQuestionId, [])}
            >
              Clear Selection
            </Button>
          )}
        </Stack>
      </Box>

      {/* Existing Clues – accordion (collapsible, first N shown to avoid lag) */}
      {clues.length > 0 ? (
        <Accordion
          expanded={accordionOpen}
          onChange={() => setAccordionOpen((prev) => !prev)}
          sx={{
            backgroundColor: "rgba(255,255,255,0.05)",
            color: "#fff",
            "&:before": { display: "none" },
            "& .MuiAccordionSummary-root": { minHeight: 48 },
            "& .MuiAccordionSummary-content": { my: 1 },
            "& .MuiAccordionDetails-root": { pt: 0, pb: 1 },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: "#4CAF50" }} />}
            aria-controls="existing-clues-content"
            id="existing-clues-header"
          >
            <Typography variant="subtitle2" sx={{ color: "#4CAF50", fontWeight: 600 }}>
              Existing Clues ({clues.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {visibleClues.map((clue) => (
              <SongClueCard key={clue.id} sx={{ mb: 1 }}>
                <Avatar
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "8px",
                    backgroundColor: "#333",
                  }}
                >
                  <MusicIcon />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ color: "#fff", fontWeight: 600, mb: 0.5 }}
                      >
                        {clue.song_name}
                      </Typography>
                      {clue.song_artist && (
                        <Typography
                          variant="body2"
                          sx={{ color: "#aaa", mb: 1 }}
                        >
                          {clue.song_artist}
                        </Typography>
                      )}
                      <Typography
                        variant="body2"
                        sx={{ color: "#ccc", fontStyle: "italic" }}
                      >
                        {clue.clue_text}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() =>
                        onDeleteClue(tempQuestionId, clue.songs_master_id)
                      }
                      sx={{ color: "#f44336" }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </SongClueCard>
            ))}
            {clues.length > CLUES_INITIAL_SHOW && (
              <Button
                size="small"
                variant="text"
                fullWidth
                onClick={() => setCluesExpanded((prev) => !prev)}
                startIcon={
                  showAllClues ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )
                }
                sx={{ mt: 1, textTransform: "none", color: "#4CAF50" }}
              >
                {showAllClues
                  ? "Show less"
                  : `Show more (${hiddenCount} more)`}
              </Button>
            )}
          </AccordionDetails>
        </Accordion>
      ) : (
        <Typography variant="body2" sx={{ color: "#888", fontStyle: "italic" }}>
          No clues generated yet. Select songs above and click "Generate Clues".
        </Typography>
      )}
    </Box>
  );
};

export default SongCluesSection;
